import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { formatToLocalDate } from '../../utils/dateUtils'
import { 
  PlusIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  UserIcon,
  BriefcaseIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlayIcon,
  PauseIcon,
  EyeIcon
} from '@heroicons/react/24/outline'

import { interviewsApi } from '../../services/api'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import CreateInterviewModal from '../../components/interview/CreateInterviewModal'

interface InterviewSession {
  id: string
  session_id: string
  job_id: string
  resume_id: string
  interviewer_id: string
  status: string
  interview_type: string
  max_questions: number
  target_duration_minutes: number
  confidence_threshold: number
  scheduled_at?: string
  started_at?: string
  completed_at?: string
  actual_duration_minutes?: number
  overall_score?: number
  hiring_recommendation?: string
  confidence_level?: number
  created_at: string
  updated_at: string
}

const InterviewsPage: React.FC = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [selectedStatus, setSelectedStatus] = useState<string>('all')
  const [selectedType, setSelectedType] = useState<string>('all')

  // Fetch interviews
  const { data: interviews, isLoading, error, refetch } = useQuery<InterviewSession[]>({
    queryKey: ['interviews', selectedStatus, selectedType],
    queryFn: async () => {
      const params: any = {}
      if (selectedStatus !== 'all') params.status = selectedStatus
      if (selectedType !== 'all') params.interview_type = selectedType
      
      const response = await interviewsApi.list(params)
      return response.data
    },
  })

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />
      case 'in_progress':
        return <PlayIcon className="w-5 h-5 text-blue-500" />
      case 'cancelled':
        return <XCircleIcon className="w-5 h-5 text-red-500" />
      case 'failed':
        return <XCircleIcon className="w-5 h-5 text-red-600" />
      default:
        return <PauseIcon className="w-5 h-5 text-gray-500" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800'
      case 'in_progress':
        return 'bg-blue-100 text-blue-800'
      case 'cancelled':
        return 'bg-red-100 text-red-800'
      case 'failed':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getRecommendationColor = (recommendation: string) => {
    switch (recommendation) {
      case 'strong_hire':
        return 'bg-green-100 text-green-800'
      case 'hire':
        return 'bg-green-50 text-green-700'
      case 'maybe':
        return 'bg-yellow-100 text-yellow-800'
      case 'no_hire':
        return 'bg-red-50 text-red-700'
      case 'strong_no_hire':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const formatDuration = (minutes?: number) => {
    if (!minutes) return 'N/A'
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    if (hours > 0) {
      return `${hours}h ${mins}m`
    }
    return `${mins}m`
  }

  const handleCreateInterview = () => {
    setIsCreateModalOpen(true)
  }

  const handleInterviewCreated = () => {
    setIsCreateModalOpen(false)
    refetch()
    toast.success('Interview session created successfully!')
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <XCircleIcon className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-red-800 mb-2">Error Loading Interviews</h3>
        <p className="text-red-600 mb-4">
          Failed to load interview sessions. Please try again.
        </p>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Virtual Interviews</h1>
          <p className="text-gray-600 mt-1">
            Conduct AI-powered adaptive interviews with intelligent questioning
          </p>
        </div>
        <button
          onClick={handleCreateInterview}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          New Interview
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex space-x-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="not_started">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Interview Type
            </label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Types</option>
              <option value="technical">Technical</option>
              <option value="behavioral">Behavioral</option>
              <option value="final">Final</option>
              <option value="screening">Screening</option>
            </select>
          </div>
        </div>
      </div>

      {/* Interviews List */}
      <div className="bg-white rounded-lg border border-gray-200">
        {!interviews || interviews.length === 0 ? (
          <div className="text-center py-12">
            <ChatBubbleLeftRightIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No interviews yet</h3>
            <p className="text-gray-600 mb-6">
              Start conducting AI-powered interviews to evaluate candidates
            </p>
            <button
              onClick={handleCreateInterview}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create Your First Interview
            </button>
          </div>
        ) : (
          <div className="overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Session
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Score
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recommendation
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {interviews.map((interview) => (
                  <tr key={interview.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(interview.status)}
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {interview.session_id}
                          </div>
                          <div className="text-sm text-gray-500" title={interview.job_id}>
                            Job: {interview.job_id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(interview.status)}`}>
                        {interview.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {interview.interview_type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <ClockIcon className="w-4 h-4 mr-1 text-gray-400" />
                        {formatDuration(interview.actual_duration_minutes)}
                        {interview.target_duration_minutes && (
                          <span className="text-gray-500 ml-1">
                            / {formatDuration(interview.target_duration_minutes)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {interview.overall_score ? (
                        <div className="flex items-center">
                          <div className="text-sm font-medium text-gray-900">
                            {Math.round(interview.overall_score * 100)}%
                          </div>
                          {interview.confidence_level && (
                            <div className="text-xs text-gray-500 ml-1">
                              ({Math.round(interview.confidence_level * 100)}% conf.)
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {interview.hiring_recommendation ? (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRecommendationColor(interview.hiring_recommendation)}`}>
                          {interview.hiring_recommendation.replace('_', ' ')}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatToLocalDate(interview.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        {interview.status === 'in_progress' && (
                          <button
                            onClick={() => window.location.href = `/interviews/${interview.session_id}/conduct`}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Continue
                          </button>
                        )}
                        {interview.status === 'not_started' && (
                          <button
                            onClick={() => window.location.href = `/interviews/${interview.session_id}/conduct`}
                            className="text-green-600 hover:text-green-900"
                          >
                            Start
                          </button>
                        )}
                        <button
                          onClick={() => window.location.href = `/interviews/${interview.session_id}`}
                          className="text-gray-600 hover:text-gray-900"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Interview Modal */}
      <CreateInterviewModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onInterviewCreated={handleInterviewCreated}
      />
    </div>
  )
}

export default InterviewsPage